import { apiSlice } from './apiSlice';

import { decodeToken, encodeToken } from '../../utils/TokenUtils';

export const patientApi = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getEmergencyButtonData: builder.query({
			query: (imei) => `/api/v1/emergency-buttons/get/all/data/${imei}`,
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				return token ? decodeToken(token) : (response?.data || {});
			},
			providesTags: (result, error, imei) => [{ type: 'EmergencyButtonData', id: imei }],
		}),
		getWatchData: builder.query({
			query: (imei) => `/api/v1/watch/get/all/data/${imei}`,
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				return token ? decodeToken(token) : (response?.data || {});
			},
			providesTags: (result, error, imei) => [{ type: 'WatchData', id: imei }],
		}),
		getPatientVitals: builder.query({
				query: (args) => {
					const patientId = typeof args === 'string' ? args : args?.patientId;
					const initiateDate = (typeof args === 'object' && args?.initiateDate)
						|| new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
					const endDate = (typeof args === 'object' && args?.endDate)
						|| new Date().toISOString();
					const source = typeof args === 'object' ? args?.source : undefined;
					const body: any = { initiateDate, endDate };
					if (source) body.source = source;
					return {
						url: `/api/v1/patient/get/all/vitalsData/${patientId}`,
						method: 'POST',
						body,
					};
				},
				transformResponse: (response: any) => {
					const token = response?.token ?? response?.data?.token;
					return token ? decodeToken(token) : (response?.data || {});
				},
				providesTags: (result, error, args) => {
					const id = typeof args === 'string' ? args : args?.patientId;
					return [{ type: 'Vitals', id }];
				},
		}),
		
		
		getAllPatients: builder.query({
			query: ({ silent = true } = {}) => ({
				url: `/api/v1/patients`,
				// If silent was a custom axios config, we can ignore it here or pass headers
			}),
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				if (token) {
					const decoded = decodeToken(token);
					return Array.isArray(decoded) ? decoded : (decoded?.patients ?? decoded ?? []);
				}
				const payload = response?.data ?? response?.message ?? [];
				return Array.isArray(payload) ? payload : [];
			},
			providesTags: ['Patient'],
		}),
getPatientsPaginated: builder.query({
			query: (params) => {
				const { page = 1, limit = 10, search = '', showActive = true, deviceFilter = 'all', centerId } = params || {};
				return {
					url: `/api/v1/patients/paginated`,
					params: { page, limit, search, showActive, deviceFilter, centerId }
				};
			},
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				return token ? decodeToken(token) : (response?.data || { data: [], totalPages: 1, currentPage: 1, totalItems: 0 });
			},
			providesTags: ['Patient'],
		}),
		
		createPatient: builder.mutation({
			query: (data) => {
				const { photoPatient, patientId, ...rest } = data;
				const token = encodeToken(rest);
				const formData = new FormData();
				if (photoPatient instanceof File) {
					formData.append('file', photoPatient);
				}
				formData.append('token', token);
				return {
					url: `/api/v1/patients`,
					method: 'POST',
					body: formData,
				};
			},
			invalidatesTags: ['Patient'],
		}),

		updatePatient: builder.mutation({
			query: ({ patientId, data }) => ({
				url: `/api/v1/patients/update/${patientId}`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: (result, error, { patientId }) => [
				{ type: 'Patient', id: patientId },
				'Patient'
			],
		}),
getPatient: builder.query({
			query: (patientId) => `/api/v1/patients/${patientId}`,
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				return token ? decodeToken(token) : (response?.data || {});
			},
			providesTags: (result, error, id) => [{ type: 'Patient', id }],
		}),
		getPatientReports: builder.query({
			query: (dni) => `/api/v1/reports/${dni}`,
			transformResponse: (response: any) => {
				const data = response?.data ?? response?.message ?? response;
				return Array.isArray(data) ? data : [];
			},
			providesTags: (result, error, dni) => [{ type: 'Patient' as const, id: `reports-${dni}` }],
		}),
		uploadPatientReport: builder.mutation({
			query: (args: any) => {
				const { dni, file, formData, ...rest } = args;
				let body = formData;
				if (!body && file) {
					body = new FormData();
					body.append('file', file);
					body.append('dni', dni);
					const meta = rest.meta || rest;
					if (meta?.type) body.append('type', meta.type);
					if (meta?.title) body.append('title', meta.title || file?.name || 'Report');
					if (meta?.timestamp) body.append('timestamp', meta.timestamp);
				}
				return {
					url: `/api/v1/reports/${dni}`,
					method: 'POST',
					body,
				};
			},
			invalidatesTags: (result, error, { dni }) => [{ type: 'Patient' as const, id: `reports-${dni}` }],
		}),
		getPatientLocations: builder.query({
			query: ({ patientId, limit = 50 }: { patientId: string; limit?: number }) =>
				`/api/v1/patients/locations/${patientId}?limit=${limit}`,
			transformResponse: (response: any) => {
				const data = response?.data ?? response?.message ?? response;
				return Array.isArray(data) ? data : [];
			},
			providesTags: (result, error, { patientId }) => [{ type: 'Patient' as const, id: `locations-${patientId}` }],
		}),
		reconfigureSos: builder.mutation({
			query: (patientId) => ({
				url: `/api/v1/patients/reconfigure/${patientId}`,
				method: 'POST',
			}),
		}),
		deletePatient: builder.mutation({
			query: (patientId) => ({
				url: `/api/v1/patients/${patientId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Patient'],
		}),
		restorePatient: builder.mutation({
			query: (patientId) => ({
				url: `/api/v1/patients/restore/${patientId}`,
				method: 'PATCH',
			}),
			invalidatesTags: ['Patient'],
		}),
		getReportsRange: builder.query({
			query: ({ dni, from, to }: { dni: string; from: string; to: string }) => ({
				url: `/api/v1/reports/${dni}/range`,
				params: { from, to },
			}),
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				if (token) return decodeToken(token);
				return Array.isArray(response?.data) ? response.data : (response || []);
			},
			providesTags: (result, error, { dni }) => [{ type: 'PatientReports', id: dni }],
		}),
	}),
});

export const {
	useCreatePatientMutation,
	useGetAllPatientsQuery,
	useGetPatientVitalsQuery,
	useGetWatchDataQuery,
	useGetEmergencyButtonDataQuery,
	useGetPatientQuery,
	useGetPatientsPaginatedQuery,
	useUpdatePatientMutation,
	useGetPatientReportsQuery,
	useUploadPatientReportMutation,
	useGetPatientLocationsQuery,
	useReconfigureSosMutation,
	useDeletePatientMutation,
	useRestorePatientMutation,
	useGetReportsRangeQuery,
} = patientApi;
